/**
 * JUWARI 商品ページの購入数量上限。
 *
 * 上限セット数そのもの（10本入り=2 / 3本入り=3）は Liquid が数量入力欄の
 * max / data-juwari-qty-max として出力しており、
 * 「＋が止まる」「max を超える値が入らない」はテーマ標準の
 *   - global.js の <quantity-input>（stepUp() が max で頭打ち＋ボタンに .disabled）
 *   - product-info.js の setQuantityBoundries()
 * がそのまま担当する。このファイルが追加で受け持つのは次の 2 点だけ。
 *
 *  1. 数値を直接入力された場合に max / min の範囲へ丸める
 *     （テーマ標準は商品ページでは値を丸めないため）
 *  2. 「カートに追加」のときだけ、カート内の同一バリアントの数量と合算して
 *     上限を超えないか確認し、超える場合は追加を中止して案内を出す
 *
 * 「今すぐ購入」(cart-create-checkout.js) は Storefront API cartCreate で
 * 既存カートと独立した別注文を作るため、カート内数量の影響を受けない
 * （入力欄の上限 2 / 3 だけが効く）。
 *
 * 単品購入と定期購入は同一バリアントに Selling Plan が付くかどうかの違いしか
 * ないため、合算はバリアント単位で行う（Selling Plan では分けない）。
 *
 * 既存コードは書き換えない。submit / click を capture フェーズで先回りして
 * 判定するだけなので、product-form.js・Mikawaya・cart-create-checkout.js の
 * 処理には手を入れていない。
 */
if (typeof window.JuwariQuantityLimit === 'undefined') {
  window.JuwariQuantityLimit = (function () {
    // 検証済みの再送信を素通りさせるためのフラグ（フォーム要素に持たせる）
    const RESUBMIT_FLAG = 'juwariQtyLimitChecked';
    const pendingForms = new WeakSet();

    /**
     * フォームに紐づく数量入力欄を返す。
     * 数量入力欄は <product-form> の外（quantity_selector ブロック）にあり、
     * form 属性でフォームに紐づいている点に注意。
     */
    function quantityInputFor(form) {
      if (!form) return null;

      // form.id は使えない。HTMLFormElement は name 付きコントロールを自身の
      // プロパティとして公開するため、商品フォーム内の <input name="id">
      // （バリアント ID）が form.id を上書きして要素オブジェクトを返してしまう。
      const formId = form.getAttribute('id');
      if (formId) {
        const byFormAttr = document.querySelector('[name="quantity"][form="' + formId + '"]');
        if (byFormAttr) return byFormAttr;
      }

      // 予備1: <product-form data-section-id> と入力欄の id="Quantity-{section}" で引く
      const productForm = form.closest('product-form');
      const sectionId = productForm && productForm.dataset.sectionId;
      if (sectionId) {
        const bySection = document.getElementById('Quantity-' + sectionId);
        if (bySection) return bySection;
      }

      // 予備2: 同じ商品セクション内の数量入力欄
      const scope = form.closest('product-info') || document;
      return scope.querySelector('.product-form__quantity .quantity__input[name="quantity"]');
    }

    /** JUWARI の上限が設定されている入力欄なら上限値を返す。対象外は null。 */
    function limitOf(input) {
      if (!input) return null;
      const max = parseInt(input.dataset.juwariQtyMax, 10);
      return Number.isInteger(max) && max > 0 ? max : null;
    }

    /** 入力値を min〜max に丸める。丸めた場合のみ change を発火する。 */
    function clamp(input) {
      const max = limitOf(input);
      if (max === null) return;

      const min = parseInt(input.min, 10) || 1;
      let value = parseInt(input.value, 10);
      if (!Number.isInteger(value)) value = min;

      const clamped = Math.min(Math.max(value, min), max);
      if (String(clamped) !== String(input.value)) {
        input.value = clamped;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // バリアントごとの「最後に確認できたカート内数量」。
    // 初期値はページ描画時点の Liquid の値（data-juwari-cart-qty）で、
    // /cart.js が読めたときに更新する。通信失敗時の保険に使う。
    const lastKnownCartQty = new Map();

    /** カート内にある同一バリアントの合計数量（単品・定期を合算）。 */
    async function cartQuantityForVariant(variantId) {
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(rootUrl + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('cart.js HTTP ' + response.status);
      const cart = await response.json();

      const total = (cart.items || []).reduce(function (sum, item) {
        return String(item.variant_id) === String(variantId) ? sum + item.quantity : sum;
      }, 0);

      lastKnownCartQty.set(String(variantId), total);
      return total;
    }

    function message(max, inCart, remaining) {
      const head = '1回のご注文でご購入いただけるのは最大' + max + 'セットまでです。';
      if (remaining <= 0) {
        return head + 'すでにカートに' + inCart + 'セット入っているため、これ以上追加できません。';
      }
      return head + 'すでにカートに' + inCart + 'セット入っているため、あと' + remaining + 'セットまで追加できます。';
    }

    /**
     * product-form.js の handleErrorMessage() と同じ場所・同じ出し方でメッセージを出す。
     * （product-form.js 側は次回の追加時にこの要素を空に戻すため、後始末は不要）
     */
    function showError(form, text) {
      const productForm = form.closest('product-form');
      const wrapper = productForm && productForm.querySelector('.product-form__error-message-wrapper');
      if (!wrapper) {
        window.alert(text);
        return;
      }
      const messageEl = wrapper.querySelector('.product-form__error-message');
      if (messageEl) messageEl.textContent = text;
      wrapper.toggleAttribute('hidden', false);
    }

    function clearError(form) {
      const productForm = form.closest('product-form');
      const wrapper = productForm && productForm.querySelector('.product-form__error-message-wrapper');
      if (wrapper) wrapper.toggleAttribute('hidden', true);
    }

    async function checkThenSubmit(form, input, max) {
      const variantId = form.querySelector('[name="id"]') ? form.querySelector('[name="id"]').value : null;
      const quantity = parseInt(input.value, 10) || 1;

      let inCart;
      try {
        inCart = await cartQuantityForVariant(variantId);
      } catch (e) {
        // 通信に失敗した場合は、ページ描画時点の Liquid の値（または直近で
        // 確認できた値）で判定する。それも無い場合だけ通す
        // （カート取得の失敗だけで購入を止めると機会損失になるため。
        //  その場合もカートページ側の上限で最終的に止まる）。
        const fallback = lastKnownCartQty.has(String(variantId))
          ? lastKnownCartQty.get(String(variantId))
          : parseInt(input.dataset.juwariCartQty, 10);

        if (!Number.isInteger(fallback)) {
          console.warn('[JuwariQuantityLimit] カート内数量を取得できなかったため合算判定を省略します', e);
          resubmit(form);
          return;
        }
        console.warn('[JuwariQuantityLimit] cart.js を取得できないため、ページ描画時点の数量で判定します', e);
        inCart = fallback;
      }

      const remaining = Math.max(max - inCart, 0);
      if (quantity > remaining) {
        showError(form, message(max, inCart, remaining));
        if (remaining > 0) {
          input.value = remaining;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        pendingForms.delete(form);
        return;
      }

      clearError(form);
      resubmit(form);
    }

    function resubmit(form) {
      form.dataset[RESUBMIT_FLAG] = 'true';
      pendingForms.delete(form);
      form.requestSubmit();
    }

    // --- 「カートに追加」: capture フェーズで product-form.js より先に判定する ---
    document.addEventListener(
      'submit',
      function (event) {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.dataset.type !== 'add-to-cart-form') return;

        // 検証済みの再送信は素通りさせる
        if (form.dataset[RESUBMIT_FLAG] === 'true') {
          delete form.dataset[RESUBMIT_FLAG];
          return;
        }

        const input = quantityInputFor(form);
        const max = limitOf(input);
        if (max === null) return;

        // 判定が終わるまで product-form.js の送信処理には渡さない
        event.preventDefault();
        event.stopPropagation();

        if (pendingForms.has(form)) return;
        pendingForms.add(form);

        clamp(input);
        checkThenSubmit(form, input, max);
      },
      true
    );

    // --- 「今すぐ購入」: 別注文なのでカート内数量は見ず、入力値だけ丸める ---
    document.addEventListener(
      'click',
      function (event) {
        const button = event.target.closest && event.target.closest('[data-buy-now="true"]');
        if (!button) return;

        const productForm = button.closest('product-form');
        const form = productForm && productForm.querySelector('form');
        clamp(quantityInputFor(form));
      },
      true
    );

    // --- 直接入力への丸め ---
    document.addEventListener('change', function (event) {
      const input = event.target;
      if (!input || input.name !== 'quantity') return;
      clamp(input);
    });

    return { clamp: clamp, cartQuantityForVariant: cartQuantityForVariant };
  })();
}
